// This module must stay browser-safe and free of DB/auth dependencies.
export * from "./tenderListing";
export * from "./platformIntro";
export const SITE_ORIGIN = "https://www.skoltend.com";
export type SchemaNode = Record<string, unknown>;

export function serializeJsonLd(value: unknown) {
  return JSON.stringify(value).replace(/</g, "\\u003c").replace(/>/g, "\\u003e")
    .replace(/&/g, "\\u0026").replace(/\u2028/g, "\\u2028").replace(/\u2029/g, "\\u2029");
}

export function siteGraph(nodes: SchemaNode[] = []) {
  return { "@context": "https://schema.org", "@graph": [
    { "@type": "Organization", "@id": `${SITE_ORIGIN}/#organization`, name: "Skoltend", url: `${SITE_ORIGIN}/`,
      logo: `${SITE_ORIGIN}/Logo-shield-192x192.png` },
    { "@type": "WebSite", "@id": `${SITE_ORIGIN}/#website`, name: "Skoltend", url: `${SITE_ORIGIN}/`,
      inLanguage: "zh-HK", publisher: { "@id": `${SITE_ORIGIN}/#organization` } },
    ...nodes,
  ] };
}

export function breadcrumbs(items: { name: string; path: string }[]): SchemaNode {
  return { "@type": "BreadcrumbList", itemListElement: items.map((item, index) => ({
    "@type": "ListItem", position: index + 1, name: item.name, item: `${SITE_ORIGIN}${item.path}`,
  })) };
}

export function tenderSlug(id: number, title: string) {
  const slug = title.replace(/\s+/g, "-").replace(/[^\p{L}\p{N}-]/gu, "")
    .replace(/-+/g, "-").replace(/^-|-$/g, "").slice(0, 80);
  return `${id}-${slug || "tender"}`;
}

export interface PublicTender {
  id: number;
  title: string;
  district: string;
  status: string;
  deadlineDate: string;
  deadlineTime?: string | null;
  createdAt?: string;
  categories?: { slug: string; nameChinese?: string; name?: string }[];
}

// Never spread API/DB tender objects into HTML, JSON-LD or transfer state.
// The input may belong to a paid viewer: discard ALL non-public fields.
export function publicTender(input: PublicTender): PublicTender {
  return {
    id: input.id, title: input.title, district: input.district, status: input.status,
    deadlineDate: input.deadlineDate, deadlineTime: input.deadlineTime ?? null,
    ...(input.createdAt ? { createdAt: input.createdAt } : {}),
    categories: (input.categories ?? []).map(c => ({ slug: c.slug, name: c.name, nameChinese: c.nameChinese })),
  };
}

export function tenderSchema(input: PublicTender): SchemaNode[] {
  const tender = publicTender(input);
  const path = `/tenders/${encodeURIComponent(tenderSlug(tender.id, tender.title))}`;
  const url = SITE_ORIGIN + path;
  const deadline = /^\d{4}-\d{2}-\d{2}$/.test(tender.deadlineDate)
    ? tender.deadlineDate + (/^\d{2}:\d{2}$/.test(tender.deadlineTime ?? "") ? `T${tender.deadlineTime}:00+08:00` : "") : undefined;
  const status = tender.status === "open" ? "招標中" : "已截止";
  return [
    { "@type": "WebPage", "@id": `${url}#webpage`, url, name: `${tender.title}｜Skoltend`,
      inLanguage: "zh-HK", isPartOf: { "@id": `${SITE_ORIGIN}/#website` }, mainEntity: { "@id": `${url}#notice` } },
    { "@type": "CreativeWork", "@id": `${url}#notice`, name: tender.title, identifier: String(tender.id),
      inLanguage: "zh-HK", genre: "學校採購及招標公告",
      description: `${tender.district}。${status}。截止日期：${tender.deadlineDate}${tender.deadlineTime ? ` ${tender.deadlineTime}` : ""}。學校身份、聯絡資料及文件僅供付費會員查看。`,
      spatialCoverage: { "@type": "Place", name: `香港${tender.district}` },
      about: [
        ...(tender.categories ?? []).map(c => ({ "@type": "DefinedTerm", name: c.nameChinese ?? c.name ?? c.slug, termCode: c.slug })),
        ...(deadline ? [{ "@type": "PropertyValue", name: "投標截止時間", propertyID: "submissionDeadline", value: deadline }] : []),
        { "@type": "PropertyValue", name: "招標狀態", propertyID: "tenderStatus", value: tender.status },
      ],
      mainEntityOfPage: { "@id": `${url}#webpage` },
      // Deliberately no issuer/school, description, documents, source URL,
      // datePublished or createdAt->datePublished mapping.
    },
    breadcrumbs([{ name: "首頁", path: "/" }, { name: "招標列表", path: "/tenders" }, { name: tender.title, path }]),
  ];
}

export function collectionSchema(path: string, name: string, items: { name: string; path: string }[]): SchemaNode[] {
  return [{ "@type": "CollectionPage", "@id": `${SITE_ORIGIN}${path}#webpage`, url: `${SITE_ORIGIN}${path}`,
    name, inLanguage: "zh-HK", isPartOf: { "@id": `${SITE_ORIGIN}/#website` },
    mainEntity: { "@type": "ItemList", numberOfItems: items.length,
      itemListElement: items.map((item, i) => ({ "@type": "ListItem", position: i + 1, name: item.name, url: SITE_ORIGIN + item.path })) } },
    breadcrumbs([{ name: "首頁", path: "/" }, { name, path }])];
}

export interface PublicSchool { id: number; name: string; district: string; schoolType?: string; }
export function schoolsSchema(schools: PublicSchool[]): SchemaNode[] {
  return [{ "@type": "CollectionPage", "@id": `${SITE_ORIGIN}/schools#webpage`, url: `${SITE_ORIGIN}/schools`,
    name: "覆蓋學校", mainEntity: { "@type": "ItemList", numberOfItems: schools.length,
      itemListElement: schools.map((s, i) => ({ "@type": "ListItem", position: i + 1, item: {
        "@type": "EducationalOrganization", "@id": `${SITE_ORIGIN}/schools#school-${s.id}`,
        name: s.name, address: { "@type": "PostalAddress", addressLocality: s.district, addressCountry: "HK" },
      } })) } },
    breadcrumbs([{ name: "首頁", path: "/" }, { name: "覆蓋學校", path: "/schools" }])];
}

export interface PublicArticle {
  slug: string; title: string; description: string; publishedAt: string; updatedAt?: string | Date;
  authorName?: string | null; reviewedAt?: string | null; sourceUrls?: string[];
  authorType?: string;
  coverImage?: string;
}

export function safeSourceUrls(values: string[] = []) {
  return [...new Set(values.filter(value => {
    try { const url = new URL(value); return ["http:", "https:"].includes(url.protocol) && !url.username && !url.password; }
    catch { return false; }
  }))];
}

export function articleSchema(article: PublicArticle): SchemaNode[] {
  const path = `/guides/${encodeURIComponent(article.slug)}`;
  const url = SITE_ORIGIN + path;
  return [{ "@type": "Article", "@id": `${url}#article`, headline: article.title,
    description: article.description, url, inLanguage: "zh-HK",
    datePublished: article.publishedAt,
    ...(article.updatedAt ? { dateModified: new Date(article.updatedAt).toISOString() } : {}),
    ...(article.authorName ? { author: { "@type": article.authorType === "Person" ? "Person" : "Organization", name: article.authorName } } : {}),
    ...(article.coverImage ? { image: new URL(article.coverImage, SITE_ORIGIN).href } : {}),
    publisher: { "@id": `${SITE_ORIGIN}/#organization` },
    mainEntityOfPage: { "@type": "WebPage", "@id": url, ...(article.reviewedAt ? { lastReviewed: article.reviewedAt } : {}) },
    citation: safeSourceUrls(article.sourceUrls),
  }, breadcrumbs([{ name: "首頁", path: "/" }, { name: "採購指南", path: "/guides" }, { name: article.title, path }])];
}
